import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QureLayout from '../components/qure/QureLayout';
import API from '../config/api';

const QureCalendar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingDateRef = useRef(searchParams.get('date'));
  const calendarRef = useRef(null);
  const calendarInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [calendarView, setCalendarView] = useState('dayGridMonth');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const loadCalendarEvents = useCallback(async (info, successCallback, failureCallback) => {
    try {
      console.log('Loading calendar events from', info.startStr, 'to', info.endStr);
      const response = await API.get('/qure/calendar/events', {
        params: {
          start: info.startStr,
          end: info.endStr
        }
      });
      
      console.log('Calendar events response:', response.data);
      
      // Backend returns array directly
      const events = Array.isArray(response.data) ? response.data : (response.data?.events || []);
      
      if (!Array.isArray(events)) {
        console.error('Invalid events format:', events);
        successCallback([]);
        return;
      }
      
      // Format events with proper start/end times
      const formattedEvents = events.map(event => {
        try {
          const eventDate = new Date(event.start);
          const time = event.extendedProps?.scheduled_time;
          
          // Parse time if available
          let startDateTime = new Date(eventDate);
          if (time && typeof time === 'string' && time.includes(':')) {
            const [hours, minutes] = time.split(':');
            startDateTime.setHours(parseInt(hours) || 9, parseInt(minutes) || 0, 0, 0);
          } else {
            // Default to 9 AM if no time specified
            startDateTime.setHours(9, 0, 0, 0);
          }
          
          // End time is 1 hour after start
          const endDateTime = new Date(startDateTime);
          endDateTime.setHours(endDateTime.getHours() + 1);
          
          return {
            ...event,
            start: startDateTime.toISOString(),
            end: endDateTime.toISOString(),
            allDay: false
          };
        } catch (error) {
          console.error('Error formatting event:', event, error);
          // Return event as-is if formatting fails
          return {
            ...event,
            allDay: false
          };
        }
      });
      
      console.log('Formatted events:', formattedEvents.length);
      successCallback(formattedEvents);
    } catch (error) {
      console.error('Error loading calendar events:', error);
      failureCallback(error);
    }
  }, []);

  // Initialize calendar after component mounts and FullCalendar is available
  useEffect(() => {
    if (!calendarRef.current) {
      return;
    }

    if (!window.FullCalendar) {
      console.error('FullCalendar is not loaded');
      setLoading(false);
      return;
    }

    const calendarEl = calendarRef.current;
    if (!calendarEl) {
      console.error('Calendar element not found');
      setLoading(false);
      return;
    }

    // Dispose existing calendar if any
    if (calendarInstanceRef.current) {
      try {
        calendarInstanceRef.current.destroy();
      } catch (e) {
        console.log('Calendar destroy error:', e);
      }
      calendarInstanceRef.current = null;
    }

    try {
      const calendar = new window.FullCalendar.Calendar(calendarEl, {
        initialView: calendarView,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'timeGridWeek,dayGridMonth'
        },
        views: {
          timeGridWeek: {
            slotMinTime: '06:00:00',
            slotMaxTime: '22:00:00',
            slotDuration: '00:30:00',
            slotLabelInterval: '01:00:00',
            slotLabelFormat: {
              hour: 'numeric',
              minute: '2-digit',
              omitZeroMinute: false,
              meridiem: 'short'
            },
            allDaySlot: false
          },
          dayGridMonth: {
            allDaySlot: false
          }
        },
        height: 'auto',
        scrollTime: '08:00:00',
        scrollTimeReset: false,
        contentHeight: 'auto',
        aspectRatio: 1.8,
        events: loadCalendarEvents,
        dateClick: (arg) => {
          calendar.gotoDate(arg.dateStr);
          calendar.changeView('timeGridWeek', arg.dateStr);
          setCalendarView('timeGridWeek');
        },
          eventClick: async (arg) => {
            const event = arg.event;
            try {
              const response = await API.get(`/qure/calendar/schedule-details/${event.id}`);
              if (response.data.success) {
                const schedule = response.data.schedule;
                setSelectedEvent({
                  id: event.id,
                  title: event.title,
                  start: event.startStr,
                  extendedProps: {
                    ...event.extendedProps,
                    filename: schedule.filename || event.extendedProps?.filename || null
                  },
                  schedule: schedule
                });
                setShowDetailModal(true);
              }
            } catch (error) {
              console.error('Error fetching schedule details:', error);
              setSelectedEvent({
                id: event.id,
                title: event.title,
                start: event.startStr,
                extendedProps: event.extendedProps
              });
              setShowDetailModal(true);
            }
          },
        eventContent: (arg) => {
          const event = arg.event;
          const time = event.extendedProps?.scheduled_time;
          const timeStr = time ? formatTime(time) : '';
          const status = event.extendedProps?.status || 'upcoming';
          
          return {
            html: `
              <div class="qure-cal-event">
                ${timeStr ? `<div class="qure-cal-event-time">${timeStr}</div>` : ''}
                <div class="qure-cal-event-title">${escapeHtml(event.title)}</div>
                <div class="qure-cal-event-status ${status}">${status.charAt(0).toUpperCase() + status.slice(1)}</div>
              </div>
            `
          };
        },
        nowIndicator: true,
        navLinks: true,
        dayMaxEvents: 3,
        moreLinkClick: 'popover',
        viewDidMount: (viewInfo) => {
          // Update state when view changes via calendar buttons
          if (viewInfo.view.type !== calendarView) {
            setCalendarView(viewInfo.view.type);
          }
        }
      });

      calendar.render();
      calendarInstanceRef.current = calendar;
      setLoading(false);
      console.log('Calendar initialized successfully');
    } catch (error) {
      console.error('Error initializing calendar:', error);
      setLoading(false);
    }

    return () => {
      if (calendarInstanceRef.current) {
        try {
          calendarInstanceRef.current.destroy();
        } catch (e) {
          console.log('Cleanup error:', e);
        }
        calendarInstanceRef.current = null;
      }
    };
  }, [calendarView, loadCalendarEvents]);

  // Open week view for ?date= from legacy /scheduled-experiments redirects
  useEffect(() => {
    const date = pendingDateRef.current;
    if (!date || loading || !calendarInstanceRef.current) return;
    try {
      calendarInstanceRef.current.gotoDate(date);
      calendarInstanceRef.current.changeView('timeGridWeek', date);
      setCalendarView('timeGridWeek');
    } catch (error) {
      console.error('Error applying calendar date from URL:', error);
    }
    pendingDateRef.current = null;
  }, [loading]);

  // Update calendar view when it changes
  useEffect(() => {
    if (calendarInstanceRef.current && calendarView) {
      try {
        const currentView = calendarInstanceRef.current.view.type;
        if (currentView !== calendarView) {
          calendarInstanceRef.current.changeView(calendarView);
        }
      } catch (error) {
        console.error('Error changing view:', error);
      }
    }
  }, [calendarView]);

  const handleViewChange = (view) => {
    setCalendarView(view);
  };


  return (
    <QureLayout>
      <div className="qure-calendar-page">
        {loading && (
          <div className="text-center py-5" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
            <div className="qure-loading"></div>
            <p className="mt-3">Loading calendar...</p>
          </div>
        )}
        
        {/* Header */}
        <div className="qure-calendar-header">
          <div className="qure-header-content">
            <div className="qure-header-left">
              <h1>
                <i className="bi bi-calendar-event"></i>
                My Experiments Calendar
              </h1>
              <p>View and manage your scheduled experiments</p>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="qure-view-toggle-section">
          <div className="qure-view-toggle">
            <button 
              className={`qure-view-btn ${calendarView === 'timeGridWeek' ? 'active' : ''}`}
              onClick={() => handleViewChange('timeGridWeek')}
            >
              <i className="bi bi-calendar-week"></i>
              Week
            </button>
            <button 
              className={`qure-view-btn ${calendarView === 'dayGridMonth' ? 'active' : ''}`}
              onClick={() => handleViewChange('dayGridMonth')}
            >
              <i className="bi bi-calendar-month"></i>
              Month
            </button>
          </div>
        </div>

        {/* Calendar Container */}
        <div className="qure-calendar-container">
          <div ref={calendarRef} id="qureCalendar"></div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {showDetailModal && selectedEvent && (
        <div className="qure-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="qure-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="qure-modal-header">
              <h5 className="qure-modal-title">
                <i className="bi bi-flask me-2"></i>
                Experiment Details
              </h5>
              <button 
                type="button" 
                className="qure-modal-close" 
                onClick={() => setShowDetailModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="qure-modal-body">
              <div className="qure-event-detail-item">
                <strong>Experiment:</strong>
                <span>{selectedEvent.title || 'Untitled Experiment'}</span>
              </div>
              {selectedEvent.extendedProps?.project && (
                <div className="qure-event-detail-item">
                  <strong>Project:</strong>
                  <span>{selectedEvent.extendedProps.project}</span>
                </div>
              )}
              {selectedEvent.start && (
                <div className="qure-event-detail-item">
                  <strong>Scheduled Date:</strong>
                  <span>{formatDate(selectedEvent.start)}</span>
                </div>
              )}
              {selectedEvent.extendedProps?.scheduled_time && (
                <div className="qure-event-detail-item">
                  <strong>Time:</strong>
                  <span>{formatTime(selectedEvent.extendedProps.scheduled_time)}</span>
                </div>
              )}
              {selectedEvent.extendedProps?.created_by && (
                <div className="qure-event-detail-item">
                  <strong>Assigned By:</strong>
                  <span>{selectedEvent.extendedProps.created_by}</span>
                </div>
              )}
              {selectedEvent.extendedProps?.status && (
                <div className="qure-event-detail-item">
                  <strong>Status:</strong>
                  <span className={`qure-status-badge ${selectedEvent.extendedProps.status}`}>
                    {selectedEvent.extendedProps.status.charAt(0).toUpperCase() + selectedEvent.extendedProps.status.slice(1)}
                  </span>
                </div>
              )}
              {selectedEvent.schedule && selectedEvent.schedule.prt_description && (
                <div className="qure-event-detail-item full-width">
                  <strong>Description:</strong>
                  <p>{selectedEvent.schedule.prt_description}</p>
                </div>
              )}
              <div className="qure-modal-actions">
                {selectedEvent.extendedProps?.status === 'completed' && selectedEvent.extendedProps?.filename && selectedEvent.extendedProps.filename !== '' && selectedEvent.extendedProps.filename !== null ? (
                  <button 
                    className="qure-analytics-btn"
                    onClick={() => {
                      navigate(`/qure-analytics?schedule_id=${selectedEvent.id}`);
                      setShowDetailModal(false);
                    }}
                  >
                    <i className="bi bi-bar-chart-line"></i>
                    View Analytics
                  </button>
                ) : selectedEvent.extendedProps?.status === 'completed' ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#6c757d', fontSize: '0.9rem', width: '100%' }}>
                    <i className="bi bi-info-circle"></i>
                    No log file available for this experiment
                  </div>
                ) : null}
                <button 
                  className="qure-close-btn"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </QureLayout>
  );
};

export default QureCalendar;
